/**
 * Owner: Masa Bayu
 * File: design-system.ts
 * Date:Time: 2026-09-13 18:05
 * Description: 22A design-system identity + semantic foundation container.
 */
import { createQXDesignBorderSystem, isQXDesignBorderSystem, validateQXDesignBorderSystem, } from './design-border';
import { createQXColorSystem, isQXColorSystem, listQXColorRolesInTheme, validateQXColorSystem, } from './color-system';
import { createQXElevationSystem, isQXElevationSystem, validateQXElevationSystem, } from './elevation-system';
import { createQXShapeSystem, isQXShapeSystem, validateQXShapeSystem, } from './shape-system';
import { createQXSpacingSystem, isQXSpacingSystem, validateQXSpacingSystem, } from './spacing-system';
import { createQXTypographySystem, isQXTypographySystem, validateQXTypographySystem, } from './typography-system';
/** Semantic design-system model version (distinct from package QX_UI_VERSION). */
export const QX_DESIGN_SYSTEM_MODEL_VERSION = '1.0.0';
/** Opaque kinds this foundation must never own. */
export const QX_DESIGN_SYSTEM_FORBIDDEN_KEYS = [
    'react',
    'component',
    'components',
    'css',
    'className',
    'style',
    'dom',
    'html',
    'renderer',
    'layout',
    'qxw',
    'qxp',
    'widget',
];
function assertId(value, label) {
    if (typeof value !== 'string' || value.trim().length === 0 || /\s/.test(value)) {
        throw new Error(`${label} must be a non-empty id without whitespace`);
    }
}
function assertNoForbidden(record) {
    for (const key of QX_DESIGN_SYSTEM_FORBIDDEN_KEYS) {
        if (key in record) {
            throw new Error(`Design system must not include forbidden field "${key}"`);
        }
    }
}
export function createQXDesignSystemIdentity(options) {
    assertId(options.id, 'Design system id');
    if (typeof options.name !== 'string' || options.name.trim().length === 0) {
        throw new Error('Design system name must be a non-empty string');
    }
    if (typeof options.version !== 'string' || options.version.trim().length === 0) {
        throw new Error('Design system version must be a non-empty string');
    }
    return Object.freeze({
        id: options.id.trim(),
        name: options.name.trim(),
        version: options.version.trim(),
        modelVersion: QX_DESIGN_SYSTEM_MODEL_VERSION,
    });
}
export function createQXDesignSystem(options) {
    assertNoForbidden({ ...options });
    const identity = createQXDesignSystemIdentity(options.identity);
    const color = createQXColorSystem(options.color);
    const typography = createQXTypographySystem(options.typography);
    const spacing = createQXSpacingSystem(options.spacing);
    const shape = createQXShapeSystem(options.shape);
    const border = createQXDesignBorderSystem(options.border.borders);
    const elevation = createQXElevationSystem(options.elevation);
    const colorRoles = new Set(listQXColorRolesInTheme(color));
    for (const borderToken of border.borders) {
        if (borderToken.role === 'none') {
            continue;
        }
        if (!colorRoles.has(borderToken.colorRole)) {
            throw new Error(`Design border role "${borderToken.role}" references unknown colorRole "${borderToken.colorRole}"`);
        }
    }
    return Object.freeze({
        identity,
        color,
        typography,
        spacing,
        shape,
        border,
        elevation,
    });
}
export function isQXDesignSystem(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    const record = value;
    if (typeof record.identity !== 'object' || record.identity === null) {
        return false;
    }
    return (isQXColorSystem(record.color) &&
        isQXTypographySystem(record.typography) &&
        isQXSpacingSystem(record.spacing) &&
        isQXShapeSystem(record.shape) &&
        isQXDesignBorderSystem(record.border) &&
        isQXElevationSystem(record.elevation));
}
export function validateQXDesignSystem(value) {
    if (!isQXDesignSystem(value)) {
        throw new Error('Invalid design system');
    }
    assertNoForbidden({ ...value });
    createQXDesignSystemIdentity(value.identity);
    validateQXColorSystem(value.color);
    validateQXTypographySystem(value.typography);
    validateQXSpacingSystem(value.spacing);
    validateQXShapeSystem(value.shape);
    validateQXDesignBorderSystem(value.border);
    validateQXElevationSystem(value.elevation);
    createQXDesignSystem(value);
}
//# sourceMappingURL=design-system.js.map